import {inject, Injectable} from '@angular/core';
import {
  DiscountSelection,
  HarbourDuesBerthVisitForm,
  HarbourDuesContainerHandlingCountForm,
  HarbourDuesIncidentalForm
} from "../types";
import {
  HarbourDuesContainerHandlingCount,
  HarbourDuesDeclaration,
  HarbourDuesDiscount,
  PurposeCode
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {HarbourDuesService} from "./harbour-dues.service";
import {HarbourDuesContainerHandlingService} from "./harbour-dues-container-handling.service";

function isBlankCount(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function isEmptyCount(value: unknown): boolean {
  return isBlankCount(value) || Number(value) === 0;
}

export function containerCountsTableValidator(containerTable: AbstractControl): ValidationErrors | null {
  const countRows = (containerTable as FormArray).controls;
  if (countRows.length === 0) {
    return null;
  }
  return tableHasAnyCount(containerTable as FormArray) ? null : {noContainerCounts: true};
}

function tableHasAnyCount(containerTable: FormArray): boolean {
  return containerTable.controls.some(countRow =>
    !(isEmptyCount(countRow.get('full')?.value) && isEmptyCount(countRow.get('empty')?.value))
  );
}

@Injectable({
  providedIn: 'root'
})
export class HarbourDuesFormService {

  private readonly fb = inject(FormBuilder);
  private readonly harbourDuesService = inject(HarbourDuesService);
  private readonly containerHandlingService = inject(HarbourDuesContainerHandlingService);

  createForm(): FormGroup {
    return this.fb.group({
      discount: this.fb.group({
        // Amsterdam
        discountType: this.fb.control(null),
        greenAward: this.fb.control(null),
        linerService: this.fb.control(null),
        shortSea: this.fb.control(null),
        rotterdam: this.fb.group({
          isPartOfLinerService: this.fb.control(null),
          shippingCompany: this.fb.control(null),
          linerServiceType: this.fb.control(null),
          twoIsOne: this.fb.control(null),
        }),
      }),
      specialTariff: this.fb.group({
        specialTariffType: this.fb.control<string>(null),
        specialTariffReason: this.fb.control<string>(null),
      }),
      exemption: this.fb.control<string>(null),
      selectedType: this.fb.control<DiscountSelection>(DiscountSelection.NO_DISCOUNT),
      incidentals: this.fb.array<FormGroup<HarbourDuesIncidentalForm>>([]),
      berthVisits: this.fb.array<FormGroup<HarbourDuesBerthVisitForm>>([])
    });
  }

  patchForm(form: FormGroup, declaration: HarbourDuesDeclaration): void {
    form.controls['exemption'].patchValue(declaration.exemption);
    form.controls['discount'].patchValue(declaration.discount);
    form.controls['specialTariff'].patchValue(declaration.specialTariff);
    form.controls['selectedType'].patchValue(
      this.getSelectedDiscount(declaration.discount, declaration.exemption, declaration.specialTariff)
    );

    const incidentals = form.controls['incidentals'] as FormArray;
    incidentals.clear();
    (declaration.incidentals || [])
      .map(i =>
        this.fb.group<HarbourDuesIncidentalForm>({
          incidentalType: this.fb.control(i.incidentalType),
          incidentalAmount: this.fb.control(i.incidentalAmount),
          incidentalReason: this.fb.control(i.incidentalReason)
        })
      )
      .forEach(group => incidentals.push(group));

    const berthVisits = form.controls['berthVisits'] as FormArray;
    berthVisits.clear();

    declaration.visit.berthVisits
      .map(v =>
        this.fb.group<HarbourDuesBerthVisitForm>({
          berthVisitId: this.fb.control(v.berthVisitId),
          stevedore: this.fb.control(v.stevedore),
          dischargedCargoCounts: this.fb.array(
            v.dischargedCargoCounts.map(c => this.harbourDuesService.newCargoCount(c))
          ),
          loadedCargoCounts: this.fb.array(
            v.loadedCargoCounts.map(c => this.harbourDuesService.newCargoCount(c))
          ),
          dischargedGoods: this.fb.array(
            v.dischargedGoods.map(g => this.harbourDuesService.newGoodsItem(g))
          ),
          loadedGoods: this.fb.array(
            v.loadedGoods.map(g => this.harbourDuesService.newGoodsItem(g))
          ),
          loadedContainerHandling: this.newContainerHandlingList(
            v.loadedContainerHandling, this.handlesLoading(v.visitPurposes)
          ),
          dischargedContainerHandling: this.newContainerHandlingList(
            v.dischargedContainerHandling, this.handlesDischarge(v.visitPurposes)
          )
        })
      )
      .forEach(group => berthVisits.push(group));
  }

  newContainerHandlingCount(v: HarbourDuesContainerHandlingCount = null): FormGroup<HarbourDuesContainerHandlingCountForm> {
    return this.fb.group({
      containerType: this.fb.control(v?.containerType),
      full: this.fb.control(v?.full, [Validators.min(0)]),
      empty: this.fb.control(v?.empty, [Validators.min(0)])
    });
  }

  newContainerHandlingList(existing: HarbourDuesContainerHandlingCount[] = [], applies = true):
    FormArray<FormGroup<HarbourDuesContainerHandlingCountForm>> {
    const countRows = applies ?
      this.containerHandlingService.containerTypes().map(containerType => {
        const match = existing?.find(e => e.containerType === containerType);
        return this.newContainerHandlingCount(
          match ?? {containerType, full: null, empty: null}
        );
      })
      : [];
    return this.fb.array(countRows);
  }

  private handlesLoading(visitPurposes: PurposeCode[] = []): boolean {
    return visitPurposes?.includes(PurposeCode.LOADING) ?? false;
  }

  private handlesDischarge(visitPurposes: PurposeCode[] = []): boolean {
    return visitPurposes?.some(
      purpose => purpose === PurposeCode.DISCHARGE || purpose === PurposeCode.DISCHARGE_CRUDE_OIL
    ) ?? false;
  }

  private getSelectedDiscount(discount: HarbourDuesDiscount, exemption: string, specialTariff?: any): DiscountSelection {
    if (discount && (discount.discountType || discount.greenAward)) {
      return DiscountSelection.DISCOUNT;
    }
    if (discount?.rotterdam && (discount.rotterdam.linerServiceType || discount.rotterdam.shippingCompany)) {
      return DiscountSelection.DISCOUNT;
    }
    if (specialTariff?.specialTariffType) {
      return DiscountSelection.SPECIAL_FEE;
    }
    if (exemption) {
      return DiscountSelection.EXEMPTION;
    }
    return DiscountSelection.NO_DISCOUNT;
  }
}
